import React, {
  CSSProperties,
  FunctionComponent,
  ReactNode,
  RefObject,
  useState,
} from 'react';
import { useTheme } from 'styled-components';

import { BUTTON_TYPES } from '@savgroup-front-common/constants';
import { rem } from '@savgroup-front-common/primitive/helpers/units';

import { dimmerAnimation } from '../../animation/dimmerAnimation';
import {
  safeFormattedIntlString,
  SafeFormattedMessage,
  SafeFormattedMessageWithoutSpread,
} from '../../formatters';
import {
  ChevronLeftIcon,
  ChevronRightIcon,
  LoaderIcon,
} from '../../protons/icons';
import SearchInput from '../SearchInput/SearchInput';
import { VirtualList } from '../VirtualList';

import useMenu from './Menu.hooks';
import {
  $Description,
  $MenuBack,
  $MenuButton,
  $MenuButtonContainer,
  $MenuContent,
  $MenuItem,
  $MenuItemStyled,
  $MenuLink,
  $MenuSubMenu,
  $MenuTextContainer,
  $MenuTitle,
} from './Menu.styles';
import { MENU_ITEM_TYPES, MenuItem, MenuItemEnhanced } from './Menu.types';
import messages from './messages';

const VIRTUALIZATION_THRESHOLD = 20;
const VIRTUAL_LIST_CONTAINER_HEIGHT = rem(340);
const MENU_ITEM_HEIGHT_WITH_DESCRIPTION = 64;
const MENU_ITEM_HEIGHT = 48;

const estimateMenuItemSize = (menuItem: MenuItemEnhanced): number => {
  if ('description' in menuItem && menuItem.description) {
    return MENU_ITEM_HEIGHT_WITH_DESCRIPTION;
  }

  return MENU_ITEM_HEIGHT;
};

interface VerticalMenuitem {
  menuItems: MenuItem[];
  onClose?: () => void;
  dataTestId?: string;
  wrapperRef?: RefObject<any>;
  wrapperRefs?: RefObject<any>[];
  minWidth?: number;
}
const VerticalMenuContent: FunctionComponent<
  React.PropsWithChildren<VerticalMenuitem>
> = ({
  menuItems = [],
  onClose = () => undefined,
  dataTestId,
  wrapperRef,
  wrapperRefs,
  minWidth,
}) => {
  const { list } = useMenu({
    onClose,
    menuItems,
    wrapperRef,
    wrapperRefs,
  });
  const theme = useTheme();
  const [searchQuery, setSearchQuery] = useState<string>();

  const filteredList = list.filter((item) => {
    if (!searchQuery) {
      return true;
    }

    if (
      item.type === MENU_ITEM_TYPES.LINK ||
      item.type === MENU_ITEM_TYPES.MENU ||
      item.type === MENU_ITEM_TYPES.BUTTON
    ) {
      return safeFormattedIntlString(item.label)
        ?.toLowerCase()
        ?.includes(searchQuery?.toLowerCase());
    }

    return true;
  });

  const renderMenuItem = (
    menuItem: MenuItemEnhanced,
    style?: CSSProperties,
  ) => {
    let component: ReactNode;

    switch (menuItem.type) {
      case MENU_ITEM_TYPES.CUSTOM:
        component = React.cloneElement(menuItem.children, { onClose });
        break;

      case MENU_ITEM_TYPES.TITLE:
        component = (
          <$MenuTitle
            data-testid={
              dataTestId
                ? `${dataTestId}_menu_child_${MENU_ITEM_TYPES.TITLE}`
                : `menu_child_${MENU_ITEM_TYPES.TITLE}`
            }
            ref={menuItem.ref}
          >
            <$MenuContent>
              {menuItem.isLoading && (
                <LoaderIcon size="32px" color={theme.colors.mainTextColor} />
              )}
              {SafeFormattedMessage(menuItem.label)}
            </$MenuContent>
          </$MenuTitle>
        );
        break;

      case MENU_ITEM_TYPES.LINK:
        component = (
          <$MenuLink
            onClick={onClose}
            href={menuItem.href}
            ref={menuItem.ref}
            data-testid={
              dataTestId
                ? `${dataTestId}_menu_child_${MENU_ITEM_TYPES.LINK}`
                : `menu_child_${MENU_ITEM_TYPES.LINK}`
            }
            $color={menuItem.color}
            $isDisabled={menuItem.isDisabled}
            target={menuItem.target}
          >
            <$MenuContent>
              {menuItem.isLoading ? (
                <LoaderIcon size="32px" color={theme.colors.white} />
              ) : (
                menuItem.icon
              )}
              {SafeFormattedMessage(menuItem.label)}
            </$MenuContent>
          </$MenuLink>
        );
        break;

      case MENU_ITEM_TYPES.MENU:
        component = (
          <$MenuSubMenu
            onClick={menuItem.onClick}
            type={BUTTON_TYPES.BUTTON}
            disabled={menuItem.isDisabled}
            data-testid={
              dataTestId
                ? `${dataTestId}_menu_child_${MENU_ITEM_TYPES.MENU}`
                : `menu_child_${MENU_ITEM_TYPES.MENU}`
            }
            $color={menuItem.color}
          >
            {menuItem.isLoading && (
              <LoaderIcon size="20px" color={theme.colors.white} />
            )}
            {!menuItem.isLoading && menuItem.icon}

            <$MenuContent>{SafeFormattedMessage(menuItem.label)}</$MenuContent>

            <ChevronRightIcon size="20px" color={theme.colors.white} />
          </$MenuSubMenu>
        );
        break;

      case MENU_ITEM_TYPES.BACK:
        component = (
          <$MenuBack
            onClick={(e) => {
              setSearchQuery('');
              menuItem.onClick(e);
            }}
            type={BUTTON_TYPES.BUTTON}
            data-testid={
              dataTestId
                ? `${dataTestId}_menu_child_${MENU_ITEM_TYPES.BACK}`
                : `menu_child_${MENU_ITEM_TYPES.BACK}`
            }
          >
            <ChevronLeftIcon size="20px" />

            <$MenuContent>
              <SafeFormattedMessageWithoutSpread message={messages.back} />
            </$MenuContent>
          </$MenuBack>
        );
        break;

      case MENU_ITEM_TYPES.SEARCH:
        component = (
          <SearchInput
            value={searchQuery}
            onChange={(event) => setSearchQuery(event.target.value)}
            onClear={() => setSearchQuery('')}
          />
        );
        break;

      default: {
        if (menuItem.CustomLabel || menuItem.PrefixAction) {
          component = (
            <$MenuButtonContainer
              $color={menuItem.color}
              $isActive={menuItem.isActive}
              {...dimmerAnimation}
            >
              <$MenuItem
                onClick={(e: any) => {
                  const response = { ...menuItem };

                  if (menuItem.onClick) {
                    menuItem.onClick(response, e);
                  }
                }}
                data-testid={menuItem.dataTestId || 'menu_child_button'}
                disabled={menuItem.isDisabled}
                $color={menuItem.color}
                $hasSecondaryAction={!!menuItem.SecondaryAction}
              >
                {!!menuItem.PrefixAction && menuItem.PrefixAction}

                {menuItem.isLoading && (
                  <LoaderIcon size="32px" color={theme.colors.mainTextColor} />
                )}
                {!menuItem.isLoading && menuItem.icon}

                <$MenuContent>
                  <$MenuTextContainer>
                    {!!menuItem.CustomLabel && (
                      <div onClick={(e) => e.stopPropagation()}>
                        {menuItem.CustomLabel}
                      </div>
                    )}
                    {!menuItem.CustomLabel && (
                      <span title={safeFormattedIntlString(menuItem.label)}>
                        {SafeFormattedMessage(menuItem.label)}
                      </span>
                    )}

                    {menuItem.description && (
                      <$Description
                        title={safeFormattedIntlString(menuItem.description)}
                      >
                        {SafeFormattedMessage(menuItem.description)}
                      </$Description>
                    )}
                  </$MenuTextContainer>
                </$MenuContent>
              </$MenuItem>

              {!!menuItem.SecondaryAction && menuItem.SecondaryAction}
            </$MenuButtonContainer>
          );
        } else {
          component = (
            <$MenuButtonContainer $color={menuItem.color} {...dimmerAnimation}>
              <$MenuButton
                onClick={(e) => {
                  const response = { ...menuItem };

                  if (menuItem.onClick) {
                    menuItem.onClick(response, e);
                  }
                }}
                type={BUTTON_TYPES.BUTTON}
                ref={menuItem.ref}
                data-testid={menuItem.dataTestId || 'menu_child_button'}
                disabled={menuItem.isDisabled}
                $color={menuItem.color}
                $hasSecondaryAction={!!menuItem.SecondaryAction}
                $minWidth={minWidth}
              >
                {menuItem.isLoading && (
                  <LoaderIcon size="32px" color={theme.colors.mainTextColor} />
                )}
                {!menuItem.isLoading && menuItem.icon}

                <$MenuContent>
                  <$MenuTextContainer>
                    <span title={safeFormattedIntlString(menuItem.label)}>
                      {SafeFormattedMessage(menuItem.label)}
                    </span>
                    {menuItem.description && (
                      <$Description
                        title={safeFormattedIntlString(menuItem.description)}
                      >
                        {SafeFormattedMessage(menuItem.description)}
                      </$Description>
                    )}
                  </$MenuTextContainer>
                </$MenuContent>
              </$MenuButton>

              {!!menuItem.SecondaryAction && menuItem.SecondaryAction}
            </$MenuButtonContainer>
          );
        }
      }
    }

    return (
      <$MenuItemStyled
        key={menuItem.key}
        data-testid={`menu_${menuItem.key}`}
        $minWidth={minWidth}
        style={style}
      >
        {component && React.cloneElement(component)}
      </$MenuItemStyled>
    );
  };

  if (filteredList.length > VIRTUALIZATION_THRESHOLD) {
    return (
      <VirtualList
        items={filteredList}
        estimateSize={(index) => estimateMenuItemSize(filteredList[index])}
        containerHeight={VIRTUAL_LIST_CONTAINER_HEIGHT}
        renderItem={(menuItem, _index, style) =>
          renderMenuItem(menuItem, style)
        }
      />
    );
  }

  return filteredList.map((menuItem) => renderMenuItem(menuItem));
};

export default VerticalMenuContent;
